import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { showToast } from "../lib/toast"

// Listens for a fixed key sequence anywhere in the signed-in app and plays a
// short, self-cleaning animation when it is entered. Keystrokes typed into a
// form field never count. The first completion on a page is reported to
// urlValue (KeySequencesController), which texts the staff alert phones at most
// once per user; the report never raises.
//
// Honors prefers-reduced-motion: the animation is skipped and only the toast
// is shown.
export const SEQUENCE = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
  "b", "a",
]
export const RUN_MS = 3200
export const MESSAGE = "🐐 Greatest Of All Time. Now go close some leads."

const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.recent = []
    this.running = false
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    clearTimeout(this.cleanupTimer)
    this.runner?.remove()
  }

  onKeydown = (event) => {
    if (event.target instanceof Element && event.target.closest(EDITABLE)) return

    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key
    // Keep only the last SEQUENCE.length keys, so a stray extra key (↑↑↑↓…)
    // never spoils an attempt.
    this.recent = [...this.recent, key].slice(-SEQUENCE.length)
    if (this.recent.join(",") !== SEQUENCE.join(",")) return

    this.recent = []
    this.play()
  }

  play() {
    if (this.running) return

    if (!this.reducedMotion()) this.run()
    showToast(MESSAGE, "bg-success")
    this.report()
  }

  report() {
    if (this.reported || !this.hasUrlValue) return

    this.reported = true
    post(this.urlValue).catch(() => {})
  }

  run() {
    this.running = true
    const runner = document.createElement("div")
    runner.className = "gl-key-sequence-runner"
    runner.setAttribute("aria-hidden", "true")
    runner.textContent = "🐐"
    document.body.appendChild(runner)
    this.runner = runner

    this.cleanupTimer = setTimeout(() => {
      runner.remove()
      this.runner = null
      this.running = false
      this.bump()
    }, RUN_MS)
  }

  // The runner finishes against the sidebar; give it a little wobble.
  bump() {
    const sidebar = document.querySelector(".gl-sidebar")
    if (!sidebar) return

    sidebar.classList.add("gl-key-sequence-bump")
    sidebar.addEventListener("animationend", () => sidebar.classList.remove("gl-key-sequence-bump"), { once: true })
  }

  reducedMotion() {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
  }
}
